/**
 * Validator for ChooseLandmarkCard action.
 *
 * Rules (§9.2, §11.1, §14):
 *   1. stateVersion matches.
 *   2. Phase is "unlockLandmark".
 *   3. `cardId` resolves in LANDMARK_CARDS_BY_ID.
 *   4. The card's `habitat` matches a habitat in `turn.actionBudget.landmarkEligibleHabitats`.
 *   5. The active player doesn't already own this card.
 *
 * Per §9.2: "cardId is a landmark card whose habitat matches and is still
 * available in the public landmark-card pool." The card pool availability
 * check is deferred to the applier layer (requires the full remaining card
 * pool which is not currently modelled in GameState). The validator enforces
 * the cardId resolution, habitat match, and player ownership uniqueness.
 */

import { LANDMARK_CARDS_BY_ID } from "../data/landmark-cards";
import type { ChooseLandmarkCardAction } from "../types/actions";
import type { ValidationResult } from "../types/common";
import type { GameState } from "../types/game-state";
import { commonGuards, VALID } from "./common-guards";

export const validateChooseLandmarkCard = (
  state: GameState,
  action: ChooseLandmarkCardAction,
): ValidationResult => {
  const guard = commonGuards(state, action.stateVersion, "unlockLandmark");
  if (guard !== null) {
    return guard;
  }

  // cardId must exist in the registry
  const card = LANDMARK_CARDS_BY_ID[action.cardId];
  if (card === undefined) {
    return {
      valid: false,
      error: "ERR_LANDMARK_CARD_UNAVAILABLE",
      details: { cardId: action.cardId, reason: "not_found" },
    };
  }

  // Card habitat must match an eligible habitat for this turn
  const { landmarkEligibleHabitats } = state.turn.actionBudget;
  if (!landmarkEligibleHabitats.includes(card.habitat)) {
    return {
      valid: false,
      error: "ERR_LANDMARK_CARD_UNAVAILABLE",
      details: {
        cardId: action.cardId,
        cardHabitat: card.habitat,
        eligible: landmarkEligibleHabitats,
        reason: "habitat_mismatch",
      },
    };
  }

  // Player must not already own this card
  const activePlayer = state.players[state.turn.activeSeat];
  if (activePlayer === undefined) {
    return { valid: false, error: "ERR_PHASE_MISMATCH", details: {} };
  }

  if (activePlayer.landmarkPicks.includes(action.cardId)) {
    return {
      valid: false,
      error: "ERR_LANDMARK_HABITAT_TAKEN",
      details: { cardId: action.cardId },
    };
  }

  return VALID;
};
